import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import CloseIcon from 'mdi-react/CloseIcon';
import ReplayIcon from 'mdi-react/ReplayIcon';
import ChevronRightIcon from 'mdi-react/ChevronRightIcon';
import { partial } from '../../../util/translation/translation';
import { textOnColor } from '../takeoverUtils';
import { slideAction } from './SpotlightStoryPreview';
import { useClubColor, useScale } from './SpotlightTakeoverPreview';

/**
 * The image-and-text takeover (presentation.layout GENERIC) as the app opens
 * it full screen: a white sheet with the photo filling the top and fading
 * into it, then the label, headline, text (the tile's description), the
 * button and the text link. The photo settles and the text rises in; Replay
 * runs it again, and with reduced motion it shows straight away.
 * Styles: .takeover-generic in scss/component/takeover-preview.scss.
 */
const SpotlightTakeoverGenericPreview = ({ tile, compact }) => {
  const p = partial('HighlightBannerForm');
  const frameRef = useRef(null);
  const scale = useScale(frameRef, compact ? 170 : 260);
  const clubColor = useClubColor();
  // Replay: a new key mounts the scene again, which restarts it
  const [cycle, setCycle] = useState(0);

  const presentation = tile?.presentation || {};
  const image = tile?.headingImage?.croppedImage || tile?.headingImage?.preview;
  const title = `${tile?.title || ''}`.trim();
  const body = `${tile?.description || ''}`.trim();
  const eyebrow = `${presentation.eyebrow || ''}`.trim();
  // The button opens the tile's content: its own label, else the app's
  const contentId = tile?.contentType?.value ?? tile?.contentType;
  const defaultAction = slideAction(tile?.content?.value, contentId);
  const action =
    `${presentation.actionLabel || ''}`.trim() ||
    (defaultAction ? p(defaultAction) : '');
  const more = `${presentation.moreLabel || ''}`.trim();

  return (
    <div
      className={`takeover-preview takeover-preview--generic${
        compact ? ' takeover-preview--compact' : ''
      }`}
    >
      <div className="takeover-preview__frame" ref={frameRef}>
        <div
          className="takeover-preview__screen takeover-generic"
          style={{
            '--tk-club': clubColor,
            '--tk-on-club': textOnColor(clubColor),
            transform: `scale(${scale})`,
          }}
        >
          <div className="takeover-generic__scene" key={cycle}>
            <div
              className="takeover-generic__media"
              role="img"
              aria-label={title || p('takeoverPreviewImage')}
            >
              {image ? (
                <img src={image} alt="" />
              ) : (
                <span className="takeover-generic__placeholder">
                  {p('takeoverPreviewNoImage-GENERIC')}
                </span>
              )}
            </div>

            <div className="takeover-generic__body">
              {eyebrow && (
                <span className="takeover-generic__eyebrow takeover-generic__rise">
                  {eyebrow}
                </span>
              )}
              <p className="takeover-generic__headline takeover-generic__rise">
                {title || p('takeoverPreviewHeadline')}
              </p>
              {/* Takes the room left, and scrolls when the text is long */}
              <div className="takeover-generic__text takeover-generic__rise">
                {body}
              </div>
              {(action || more) && (
                <div className="takeover-generic__actions takeover-generic__rise">
                  {action && (
                    <span className="takeover-generic__button">{action}</span>
                  )}
                  {more && (
                    <span className="takeover-generic__more">
                      {more}
                      <ChevronRightIcon size={16} />
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Outside the scene, so Replay keeps its focus */}
          <div className="takeover-generic__top">
            <button
              type="button"
              className="takeover-generic__icon"
              aria-label={p('takeoverReplay')}
              title={p('takeoverReplay')}
              onClick={() => setCycle((current) => current + 1)}
            >
              <ReplayIcon size={20} />
            </button>
            <span className="takeover-generic__icon" aria-hidden>
              <CloseIcon size={20} />
            </span>
          </div>
        </div>
      </div>
      {!compact && (
        <p className="takeover-preview__caption">
          {p('takeoverPreviewCaption')}
        </p>
      )}
    </div>
  );
};

SpotlightTakeoverGenericPreview.propTypes = {
  // The tile as the form has it: title, description, headingImage, content,
  // contentType and presentation
  tile: PropTypes.shape({}),
  // Smaller, side by side with others (the group's tiles)
  compact: PropTypes.bool,
};

SpotlightTakeoverGenericPreview.defaultProps = {
  tile: null,
  compact: false,
};

export default SpotlightTakeoverGenericPreview;
